import React, { FC } from "react";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { Icon, Segment } from "semantic-ui-react";
import { DragMoveEvent, DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";
import { SqsConfigContent } from "./SQSConfig";

interface SortableItemProps {
    id: string;
    setHovered: React.Dispatch<React.SetStateAction<string | null>>;
    children: React.ReactNode;
    width: string;
    isRed: boolean;
}

export const SortableSegment: FC<SortableItemProps> = ({ id, setHovered, children, width, isRed }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    let style;
    if (transform) {
        style = {
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
            transition,
        };
    }

    return (
        <div style={style} ref={setNodeRef}>
            <Segment
                id={id}
                onMouseEnter={() => setHovered(id)}
                onMouseLeave={() => setHovered(null)}
                style={{ margin: "4px 0", width: width, minWidth: "300px", ...(isRed && { borderColor: "red" }) }}
            >
                <div style={{ display: "flex", alignItems: "center" }}>
                    <div {...attributes} {...listeners} style={{ cursor: "grab", marginRight: "8px" }}>
                        <Icon name="bars" />
                    </div>
                    {children}
                </div>
            </Segment>
        </div>
    );
};

interface SortableListProps {
    onDragMove: React.Dispatch<React.SetStateAction<DragMoveEvent | undefined>>;
    onChange: React.Dispatch<React.SetStateAction<SqsConfigContent>>;
    content: SqsConfigContent;
    items: string[];
    children: React.ReactNode;
    setIsMoving: React.Dispatch<React.SetStateAction<boolean>>;
}

const SortableList: FC<SortableListProps> = ({ onDragMove, onChange, content, items, children, setIsMoving }) => {
    const handleDragEnd = (event: any) => {
        setIsMoving(false);
        const { active, over } = event;
        if (!over) return;

        const oldIndex = items.findIndex((venueValue) => venueValue === active.id);
        const newIndex = items.findIndex((venueValue) => venueValue === over.id);

        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

        const movedItem = items[oldIndex];
        items.splice(oldIndex, 1);
        items.splice(newIndex, 0, movedItem);

        onChange(
            items.reduce(
                (sortedObject, key) => {
                    sortedObject[key] = content[key];
                    return sortedObject;
                },
                {} as Record<string, any>,
            ),
        );
    };

    return (
        <DndContext
            onDragMove={onDragMove}
            onDragEnd={handleDragEnd}
            onDragStart={() => setIsMoving(true)}
            modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
        >
            <SortableContext items={items}>{children}</SortableContext>
        </DndContext>
    );
};

export default SortableList;
